/**
 * Rendered sound as a WAV file: 16-bit PCM, every channel the buffer has, at its own rate.
 *
 * A WAV is the samples with a 44-byte header in front, which a page can write in a few lines.
 * It is the large download and the exact one: what is saved is what the panel plays. The small
 * one, to send, is an MP3 (`mp3.ts`).
 */

/** Bytes in the header in front of the samples. */
const HEADER = 44;

/** Bytes per sample: 16-bit, which every player reads. */
const DEPTH = 2;

/** The buffer's channels interleaved as 16-bit samples, behind a RIFF/WAVE header. */
export function wavOf(sound: AudioBuffer): Uint8Array {
  const channels = sound.numberOfChannels;
  const frames = sound.length;
  const size = frames * channels * DEPTH;
  const bytes = new Uint8Array(HEADER + size);
  const view = new DataView(bytes.buffer);
  const text = (at: number, value: string): void => {
    for (let i = 0; i < value.length; i += 1) view.setUint8(at + i, value.charCodeAt(i));
  };

  text(0, "RIFF");
  view.setUint32(4, HEADER - 8 + size, true);
  text(8, "WAVE");
  text(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, channels, true);
  view.setUint32(24, sound.sampleRate, true);
  view.setUint32(28, sound.sampleRate * channels * DEPTH, true);
  view.setUint16(32, channels * DEPTH, true);
  view.setUint16(34, DEPTH * 8, true);
  text(36, "data");
  view.setUint32(40, size, true);

  const waves: Float32Array[] = [];
  for (let channel = 0; channel < channels; channel += 1) waves.push(sound.getChannelData(channel));
  let at = HEADER;
  for (let frame = 0; frame < frames; frame += 1) {
    for (const wave of waves) {
      // A sample past full scale is held there, not wrapped round into a click.
      const sample = Math.max(-1, Math.min(1, wave[frame] ?? 0));
      view.setInt16(at, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      at += DEPTH;
    }
  }
  return bytes;
}
